import { GlobalStage, useGlobalStore } from "@/store/global"
import React, { useRef, useState } from "react"
import { RecallButton } from "../RecallButton"
import styles from "./index.module.scss"
import { Preloader } from "./Preloader"
import { useCheckLogin } from "@/hooks/useCheckLogin"
import { sfxPlay } from "@/assets/audios/sfx"
import { tasks } from "./task"
import { throttle } from "@/utils/utils"
import {
    useMaskEntryAnimation,
    useEntryAnimation,
    useRoleSwingAnimation,
    useRopeEntryAnimation,
    useRopeSwingAnimation,
    useChessRotationAnimation,
    useBambooEntryAnimation,
    useBambooSwingAnimation,
} from "./animation"

// ==================== 棋子配置 ====================
/** 棋子翻转方向 */
export type ChessDirection = "up" | "down" | "left" | "right"

/** 棋子配置类型 */
export interface ChessConfig {
    left: number | string // 左侧位置，可以是 px 或百分比
    top: number | string // 顶部位置，可以是 px 或百分比
    color: "white" | "black" // 初始颜色
    direction: ChessDirection // 翻转方向
}

/** 棋子配置数组 */
const CHESS_CONFIGS: ChessConfig[] = [
    { left: "calc(50% - 42rem)", top: "calc(50% - 16.875rem)", color: "black", direction: "right" },
    { left:  "calc(50% - 33.6rem)", top: "calc(50% - 16.875rem)", color: "white", direction: "down" },
    { left:  "calc(50% + 9.6rem)", top: "calc(50% - 16.875rem)", color: "black", direction: "left" },
    { left:  "calc(50% + 16.8rem)", top: "calc(50% - 16.875rem)", color: "white", direction: "right" },
    { left:  "calc(50% + 9.6rem)", top: "calc(50% - 10.125rem)", color: "white", direction: "up" },
    { left: "calc(50% + 33.24rem)", top: "calc(50% + 13.5rem)", color: "white", direction: "left" },
]

// ==================== 组件主体 ====================

const Loading = () => {
    const [finished, setFinished] = useState(false)
    const [ropeEntryFinished, setRopeEntryFinished] = useState(false)
    const [bambooLeftEntryFinished, setBambooLeftEntryFinished] =
        useState(false)
    const [bambooRightEntryFinished, setBambooRightEntryFinished] =
        useState(false)
    const globalStore = useGlobalStore()
    const checkLogin = useCheckLogin()

    const titleBgRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLDivElement>(null)
    const subTitleRef = useRef<HTMLDivElement>(null)
    const role1Ref = useRef<HTMLDivElement>(null)
    const role2Ref = useRef<HTMLDivElement>(null)
    const loadingRef = useRef<HTMLDivElement>(null)
    const maskRef = useRef<HTMLDivElement>(null)
    const ropeRef = useRef<HTMLDivElement>(null)
    const bambooLeftRef = useRef<HTMLImageElement>(null)
    const bambooRightRef = useRef<HTMLImageElement>(null)
    // 为每个棋子创建 ref
    const chessRefs = useRef(
        CHESS_CONFIGS.map(() => React.createRef<HTMLImageElement>())
    )

    // 使用自定义hooks管理动画
    // useMaskEntryAnimation(maskRef)
    useRopeEntryAnimation(ropeRef, () => {
        // rope 进入动画完成后，启动摆动动画
        setRopeEntryFinished(true)
    })
    useRopeSwingAnimation(ropeRef, ropeEntryFinished)
    useEntryAnimation(titleBgRef, titleRef, role1Ref, role2Ref, subTitleRef)
    useRoleSwingAnimation(null, role2Ref, finished)
    useChessRotationAnimation(chessRefs.current, CHESS_CONFIGS, finished)
    // 应用竹子进入动画
    useBambooEntryAnimation(bambooLeftRef, true, () => {
        setBambooLeftEntryFinished(true)
    })
    useBambooEntryAnimation(bambooRightRef, false, () => {
        setBambooRightEntryFinished(true)
    })
    // 应用竹子摆动动画
    useBambooSwingAnimation(bambooLeftRef, bambooLeftEntryFinished)
    useBambooSwingAnimation(bambooRightRef, bambooRightEntryFinished)

    /** 点击开始游戏按钮 */
    const onStart = throttle(() => {
        if (checkLogin()) {
            globalStore.setTopLayerActive(true)
        }
    })

    /** 预加载任务完成 */
    const onLoad = () => {
        setFinished(true)
    }

    return (
        <div ref={loadingRef} className={styles.loading}>
            <div className={styles.chessBoard}></div>
            {/* 棋子 */}
            {CHESS_CONFIGS.map((config, index) => {
                const chessRef = chessRefs.current[index]
                const initialSrc =
                    config.color === "white"
                        ? require("@/assets/imgs/loading/piece_white.png")
                        : require("@/assets/imgs/loading/piece_black.png")
                // 使用位置信息生成唯一 key
                const uniqueKey = `chess-${index}`
                return (
                    <img
                        key={uniqueKey}
                        ref={chessRef}
                        src={initialSrc}
                        alt=""
                        className={styles.chess}
                        style={{
                            left: config.left,
                            top: config.top,
                        }}
                    />
                )
            })}

            {/* 竹子 */}
            <img
                ref={bambooLeftRef}
                src={require("@/assets/imgs/common/bamboo_left.png")}
                alt=""
                className={styles.bambooLeft}
            />
            <img
                ref={bambooRightRef}
                src={require("@/assets/imgs/common/bamboo_right.png")}
                alt=""
                className={styles.bambooRight}
            />
            <div className={styles.decoWrapper}></div>
            <img
                className={styles.logo}
                src={require("@/assets/imgs/loading/logo_single.png")}
                alt=""
            />
            <div ref={maskRef} className={styles.bgMask} />
            {/* <img
                src={require("@/assets/imgs/loading/top.png")}
                className={styles.top}
                alt=""
            /> */}
            <div ref={titleBgRef} className={styles.titleBg}>
                <div ref={titleRef} className={styles.title} />
                {/* <div ref={subTitleRef} className={styles.subTitle} /> */}
                <div ref={role1Ref} className={styles.role1} />
                <div ref={role2Ref} className={styles.role2} />
                <Preloader
                    active
                    finished={finished}
                    onFinish={onLoad}
                    tasks={tasks}
                    onStart={onStart}
                />
            </div>
            <div ref={ropeRef} className={styles.rope} />

            {/* Recall 按钮 */}
            <RecallButton stage="loading" />
        </div>
    )
}

export default Loading
